import { LoadingContent, Spinner } from '../components/feedback';
import { useState, type FormEvent } from 'react';
import { apiSftp } from '../api/sftp';
import { usePoll } from '../hooks/usePoll';
import { errorMessage, useApp } from '../app-context';
import { ErrorAlert } from '../components/ui';
import { formatDateTime } from '../format';

export function SftpSection() {
  const settings = usePoll(() => apiSftp.settings(), 60000);
  const keys = usePoll(() => apiSftp.keys(), 60000);
  const { notify, me } = useApp();
  const [name, setName] = useState('');
  const [keyName, setKeyName] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (event: FormEvent, operation: () => Promise<unknown>) => {
    event.preventDefault();
    setBusy(true);
    try {
      await operation();
      await Promise.all([settings.reload(), keys.reload()]);
      notify('保存しました');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card stack">
      <h2 data-search-id="sftp">SSH 鍵と SFTP</h2>
      <LoadingContent loading={(!settings.data && settings.loading) || (!keys.data && keys.loading)} shape="settings">
        <ErrorAlert error={settings.error ?? keys.error} />
        <p>
          ログイン ID / SFTP 名: {settings.data?.name ?? '未設定'} / ポート: {settings.data?.port ?? '無効'}
        </p>
        <p>
          ホスト鍵の指紋: <code>{settings.data?.fingerprint ?? 'SFTP は起動していません'}</code>
        </p>
        {!me?.user?.login && (
          <form className="stack" onSubmit={(e) => void act(e, () => apiSftp.setName(name))}>
            <label htmlFor="sftp-name">SFTP 名（英小文字・数字・_・-、3〜32 文字）</label>
            <input
              id="sftp-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              pattern="[a-z0-9_-]{3,32}"
              required
            />
            <button className="btn" disabled={busy}>
              {busy && <Spinner />}
              保存
            </button>
          </form>
        )}
        <form
          className="stack"
          onSubmit={(e) =>
            void act(e, async () => {
              await apiSftp.addKey(keyName, publicKey);
              setKeyName('');
              setPublicKey('');
            })
          }
        >
          <label htmlFor="ssh-key-name">鍵の名前</label>
          <input
            id="ssh-key-name"
            className="input"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            maxLength={80}
            required
          />
          <label htmlFor="ssh-public-key">OpenSSH 公開鍵（ed25519 / ECDSA / RSA 3072 bit 以上）</label>
          <textarea
            id="ssh-public-key"
            className="input"
            rows={4}
            value={publicKey}
            onChange={(e) => setPublicKey(e.target.value)}
            required
          />
          <button className="btn" disabled={busy}>
            {busy && <Spinner />}
            鍵を追加
          </button>
        </form>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>指紋</th>
                <th>最終使用</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.data?.map((key) => (
                <tr key={key.id}>
                  <td>{key.name}</td>
                  <td>
                    <code>{key.fingerprint}</code>
                  </td>
                  <td>{key.lastUsedAt ? formatDateTime(key.lastUsedAt) : '未使用'}</td>
                  <td>
                    <button
                      className="btn small"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await apiSftp.removeKey(key.id);
                          await keys.reload();
                        } catch (error) {
                          notify(errorMessage(error), true);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      {busy && <Spinner />}
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadingContent>
    </section>
  );
}
export function SftpConnection({ serverId }: { serverId: string }) {
  const settings = usePoll(() => apiSftp.settings(), 60000);
  const { notify } = useApp();
  const value = settings.data;
  const url = value?.name && value.port ? `sftp://${value.name}.${serverId}@${value.host}:${value.port}` : undefined;
  return (
    <section className="card card-body stack">
      <h3 data-search-id="sftp-connection">SFTP 接続先</h3>
      <ErrorAlert error={settings.error} />
      {url ? (
        <div className="secret-box">
          <code style={{ flex: 1 }}>{url}</code>
          <button
            className="btn small"
            onClick={() => {
              void navigator.clipboard
                .writeText(url)
                .then(() => notify('コピーしました'))
                .catch((error: unknown) => notify(errorMessage(error), true));
            }}
          >
            コピー
          </button>
        </div>
      ) : (
        <p>設定の「SSH 鍵と SFTP」で SFTP 名と公開鍵を登録してください。SFTP の有効化は管理者が行います。</p>
      )}
    </section>
  );
}
